import {
  choiceOptionCount,
  dataToField,
  dateKindOf,
  dayFromToday,
  getDefaultValueByType,
  isChoiceFieldType,
  isCustomFieldValueBlank,
  validateCustomFieldValue,
  type FieldType,
} from "@salestrak/custom-fields";

import {
  FORM_PROPERTY_FIELD_TYPE,
  FORM_PROPERTY_LABEL,
  FORM_QUESTION_KINDS,
  type FormField,
  type FormFieldConfig,
  type FormFieldItem,
  type FormItem,
  type FormLinkedItem,
  type FormPhotoItem,
  type FormProperty,
  type FormQuestionKind,
} from "./types.ts";

export const FORM_QUESTION_FIELD_TYPE: Record<FormQuestionKind, FieldType> = {
  address: "address",
  date: "date",
  email: "email",
  longText: "paragraph",
  member: "member",
  money: "money",
  multipleChoice: "labels",
  number: "number",
  phone: "phone",
  shortText: "text",
  singleChoice: "select",
  time: "time",
  url: "url",
  yesNo: "checkbox",
};

export function questionKindForFieldType(type: string): FormQuestionKind | "attachment" | null {
  if (type === "photos") return "attachment";
  if (type === "dateTime") return "date";
  return FORM_QUESTION_KINDS.find((kind) => FORM_QUESTION_FIELD_TYPE[kind] === type) ?? null;
}

// No workspace field id starts with either prefix, so a catalog miss can tell a form's own field or a property from a deleted workspace field.
export const INLINE_FIELD_ID_PREFIX = "inline:";
export const PROPERTY_FIELD_ID_PREFIX = "property:";

export function propertyFieldId(property: FormProperty): string {
  return `${PROPERTY_FIELD_ID_PREFIX}${property}`;
}

export function propertyField(
  property: FormProperty,
  choices: readonly { value: string; label: string }[] = [],
): FormField {
  const type = FORM_PROPERTY_FIELD_TYPE[property];
  const config: FormFieldConfig = {};
  if (isChoiceFieldType(type)) {
    config.options = choices.map((choice) => ({ color: null, label: choice.label, value: choice.value }));
    config.optionSort = "manual";
  } else if (property === "assignees") {
    config.multiple = true;
    config.includeTeams = false;
  }
  return { config, customFieldId: propertyFieldId(property), name: FORM_PROPERTY_LABEL[property], type };
}

export function isFieldItem<F extends FormField>(item: FormItem<F>): item is FormFieldItem<F> {
  return (
    item.kind === "customField" || item.kind === "property" || item.kind === "question" || item.kind === "attachment"
  );
}

/** A Checkbox is one box: left alone is an answer, so it is never required. */
export function isCheckboxItem<F extends FormField>(item: FormItem<F>): boolean {
  if (item.kind === "question") return item.field.type === "checkbox";
  return item.kind === "customField" && item.fieldType === "checkbox";
}

export function isPhotoItem<F extends FormField>(item: FormItem<F>): item is FormPhotoItem<F> {
  return item.kind === "attachment" || (item.kind === "customField" && item.fieldType === "photos");
}

export function withFieldTypes<F extends FormField>(
  items: readonly FormItem<F>[],
  types: ReadonlyMap<string, FieldType>,
): FormItem<F>[] {
  return items.map((item) => {
    if (item.kind !== "customField") return item;
    const fieldType = types.get(item.customFieldId);
    return fieldType && fieldType !== item.fieldType ? { ...item, fieldType } : item;
  });
}

export function fieldItemCount<F extends FormField>(form: { items: readonly FormItem<F>[] }): number {
  return form.items.filter(isFieldItem).length;
}

export function resolveItemField<F extends FormField>(
  item: FormFieldItem<F>,
  catalog: ReadonlyMap<string, F>,
): F | null {
  if (item.kind === "customField") return catalog.get(item.customFieldId) ?? null;
  if (item.kind === "property") return catalog.get(propertyFieldId(item.property)) ?? null;
  return item.field.name === item.label ? item.field : { ...item.field, name: item.label };
}

export function choiceCount(field: Pick<FormField, "config">): number {
  return choiceOptionCount(field.config);
}

/** A field item a filler can answer: its field resolves, and a choice field
 *  has a choice. Anything else drops out of the fill and never holds Submit. */
export function isFillableItem<F extends FormField>(item: FormItem<F>, catalog: ReadonlyMap<string, F>): boolean {
  if (!isFieldItem(item)) return true;
  const field = resolveItemField(item, catalog);
  if (!field) return false;
  if (!isChoiceFieldType(field.type)) return true;
  return choiceCount(field) > 0;
}

/** The field with the form's own rules on the answer laid over it. A rule is
 *  the form's, so it never lives on a workspace field other forms share. */
export function withItemRules<F extends FormField>(item: FormFieldItem<FormField>, field: F): F {
  const requiredChoices = "requiredChoices" in item ? item.requiredChoices : undefined;
  if (!requiredChoices?.length) return field;
  return { ...field, config: { ...field.config, requiredOptions: requiredChoices } };
}

export function linkedPrefills(item: FormLinkedItem<FormField>): boolean {
  return item.prefill === true;
}

export function linkedDefaultAnswer(item: FormLinkedItem<FormField>, field: FormField | null): string[] | null {
  if (!field || !isChoiceFieldType(field.type)) return null;
  const offered = new Set((field.config?.options ?? []).flatMap((option) => (option?.value ? [option.value] : [])));
  const picks = (item.defaultChoices ?? []).filter((value) => offered.has(value));
  if (picks.length === 0) return null;
  return field.type === "select" ? picks.slice(0, 1) : picks;
}

export const TYPED_DEFAULT_TYPES: ReadonlySet<string> = new Set([
  "address",
  "date",
  "email",
  "money",
  "number",
  "paragraph",
  "phone",
  "text",
  "time",
  "url",
]);

type FormOnlyItem = Extract<FormItem<FormField>, { kind: "question" | "attachment" }>;

export function defaultsToNow(config: FormFieldConfig | null | undefined): boolean {
  return config?.defaultNow === true;
}

export function defaultNowAnswer(includeTime: boolean, now: Date = new Date()): string {
  if (!includeTime) return dayFromToday(0);
  const stamp = new Date(now);
  stamp.setSeconds(0, 0);
  return stamp.toISOString();
}

export function checkedAnswer(type: string, value: unknown): unknown {
  return type === "money" ? value : dataToField(type, value).fieldValue;
}

export function defaultValueError(item: FormOnlyItem, timeZone?: string): string | null {
  const preset = item.field.config?.defaultValue;
  if (isCustomFieldValueBlank(preset ?? null)) return null;
  const value = checkedAnswer(item.field.type, item.field.type === "select" ? [preset].flat() : preset);
  const result = validateCustomFieldValue(withItemRules(item, item.field), value, timeZone);
  return result.valid ? null : (result.message ?? "Invalid value");
}

export function invalidDefaultItemIds<F extends FormField>(items: readonly FormItem<F>[], timeZone?: string): string[] {
  return items.flatMap((item) =>
    (item.kind === "question" || item.kind === "attachment") && defaultValueError(item, timeZone) !== null
      ? [item.itemId]
      : [],
  );
}

/** A form-only question's answer before anyone touches it. A default its own
 *  question would refuse starts nothing. */
export function initialFormOnlyAnswer(item: FormOnlyItem): unknown {
  const preset = item.field.config?.defaultValue;
  const dateKind = dateKindOf(item.field);
  if ((dateKind === "date" || dateKind === "dateTime") && defaultsToNow(item.field.config)) {
    return defaultNowAnswer(dateKind === "dateTime");
  }
  if (isChoiceFieldType(item.field.type) && preset) return [preset].flat();
  if (TYPED_DEFAULT_TYPES.has(item.field.type) && !isCustomFieldValueBlank(preset) && defaultValueError(item) === null) {
    return preset;
  }
  return getDefaultValueByType(item.field.type);
}
